import { useEffect, useState, useCallback } from "react";
import { ChevronDown, ChevronUp, ArrowRight, Loader2, X } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { formatDoctorName } from "@/lib/doctorName";
import { toast } from "sonner";

// =====================================================================
// OwningDentistSection — patient profile commercial-credit panel.
// Reads list_patient_ownership_history (current owner = first row's
// to_dentist_id) and exposes Reassign / Request reassign actions
// gated by clinic role. Front desk sees the owner name only.
// =====================================================================

const REASON_OPTIONS = [
  { value: "dentist_left",       label: "Dentist left the clinic" },
  { value: "extended_leave",     label: "Owner on extended leave" },
  { value: "patient_request",    label: "Patient requested change" },
  { value: "owner_rebalance",    label: "Workload rebalance" },
  { value: "other",              label: "Other" },
];

const REASON_LABEL = Object.fromEntries(REASON_OPTIONS.map((r) => [r.value, r.label]));

function formatDateTime(ts) {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return String(ts); }
}

export default function OwningDentistSection({ patient, onChanged }) {
  const { clinicRole } = useAuth();
  const canReassign = clinicRole === "clinic_admin";
  const canRequest  = clinicRole === "doctor";
  const canViewLog  = clinicRole === "clinic_admin" || clinicRole === "doctor";

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);

  const refresh = useCallback(async () => {
    if (!patient?.id || !canViewLog) {
      setHistory([]);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase.rpc("list_patient_ownership_history", {
      p_patient_id: patient.id,
    });
    setLoading(false);
    if (error) {
      console.warn("ownership history load failed", error);
      setHistory([]);
      return;
    }
    setHistory(Array.isArray(data) ? data : []);
  }, [patient?.id, canViewLog]);

  useEffect(() => { refresh(); }, [refresh]);

  // Current owner: the most recent transfer's to_dentist_id (history is
  // returned newest-first by the RPC). Fallback "unassigned" when the
  // patient has never had a finalized visit.
  const currentOwner = history[0] || null;
  const ownerName = currentOwner?.to_dentist_name ?? null;

  function handleReassigned() {
    setReassignOpen(false);
    refresh();
    onChanged?.();
  }
  function handleRequested() {
    setRequestOpen(false);
    refresh();
  }

  return (
    <section
      className="bg-white rounded-2xl shadow-sm p-5"
      data-testid="owning-dentist-section"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
            Owning dentist
          </div>
          <div className="mt-1 text-sm font-semibold text-slate-900" data-testid="owning-dentist-name">
            {ownerName || (
              <span className="text-slate-400 font-normal">Not yet assigned · auto-set on first finalized visit</span>
            )}
          </div>
          <p className="text-[11px] text-slate-500 mt-1 max-w-md">
            Internal commercial-credit field. Not shown on patient-facing PDFs or recall messages.
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {canReassign ? (
            <button
              type="button"
              onClick={() => setReassignOpen(true)}
              data-testid="owning-dentist-reassign"
              className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 text-slate-700 bg-white hover:border-slate-400"
            >
              Reassign
            </button>
          ) : canRequest ? (
            <button
              type="button"
              onClick={() => setRequestOpen(true)}
              data-testid="owning-dentist-request"
              className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 text-slate-700 bg-white hover:border-slate-400"
            >
              Request reassign
            </button>
          ) : null}
        </div>
      </div>

      {canViewLog ? (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <button
            type="button"
            onClick={() => setHistoryOpen((v) => !v)}
            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900"
            data-testid="owning-dentist-history-toggle"
          >
            {historyOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            Ownership history {history.length > 0 ? `(${history.length})` : ""}
          </button>

          {historyOpen ? (
            <div className="mt-2" data-testid="owning-dentist-history">
              {loading ? (
                <div className="text-xs text-slate-400 inline-flex items-center gap-1.5">
                  <Loader2 className="w-3 h-3 animate-spin" /> Loading…
                </div>
              ) : history.length === 0 ? (
                <div className="text-xs text-slate-400">No transfers on record yet.</div>
              ) : (
                <ol className="space-y-2">
                  {history.map((t) => (
                    <li key={t.id} className="text-xs text-slate-600" data-testid={`ownership-history-row-${t.id}`}>
                      <span className="text-slate-400 tabular-nums">{formatDateTime(t.created_at)}</span>
                      <span className="mx-1.5 text-slate-300">·</span>
                      <span>{t.from_dentist_name || <span className="text-slate-400">unassigned</span>}</span>
                      <ArrowRight className="inline w-3 h-3 mx-1 text-slate-400" />
                      <span className="font-semibold text-slate-800">{t.to_dentist_name}</span>
                      <span className="mx-1.5 text-slate-300">·</span>
                      <span className="text-slate-500">{REASON_LABEL[t.reason] || t.reason}</span>
                      {t.actor_name ? (
                        <span className="text-slate-400"> by {t.actor_name}</span>
                      ) : null}
                      {t.notes ? (
                        <p className="mt-0.5 text-slate-500 italic">"{t.notes}"</p>
                      ) : null}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          ) : null}
        </div>
      ) : null}

      {reassignOpen ? (
        <ReassignModal
          patient={patient}
          currentOwnerId={currentOwner?.to_dentist_id ?? null}
          onClose={() => setReassignOpen(false)}
          onDone={handleReassigned}
        />
      ) : null}
      {requestOpen ? (
        <RequestModal
          patient={patient}
          currentOwnerId={currentOwner?.to_dentist_id ?? null}
          onClose={() => setRequestOpen(false)}
          onDone={handleRequested}
        />
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------
// Shared modal scaffold + dentist picker
// ---------------------------------------------------------------------
function useDoctorList() {
  const [doctors, setDoctors] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.rpc("list_ortho_doctor_records");
      if (cancelled) return;
      if (error) { setDoctors([]); return; }
      setDoctors(Array.isArray(data) ? data : []);
    })();
    return () => { cancelled = true; };
  }, []);
  return doctors;
}

function ModalShell({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" data-testid="ownership-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full inline-flex items-center justify-center hover:bg-slate-100"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="p-4 space-y-3">{children}</div>
      </div>
    </div>
  );
}

function ReassignModal({ patient, currentOwnerId, onClose, onDone }) {
  const doctors = useDoctorList();
  const [toDentistId, setToDentistId] = useState("");
  const [reason, setReason]           = useState("owner_rebalance");
  const [notes, setNotes]             = useState("");
  const [busy, setBusy]               = useState(false);

  const candidates = doctors.filter((d) => d.id !== currentOwnerId);

  async function handleSubmit() {
    if (!toDentistId) {
      toast.error("Pick a dentist.");
      return;
    }
    setBusy(true);
    const { error } = await supabase.rpc("reassign_patient_ownership", {
      p_patient_id:     patient.id,
      p_to_dentist_id:  toDentistId,
      p_reason:         reason,
      p_notes:          notes?.trim() || null,
    });
    setBusy(false);
    if (error) {
      toast.error(error.message ?? "Could not reassign.");
      return;
    }
    toast.success("Owning dentist reassigned.");
    onDone?.();
  }

  return (
    <ModalShell title="Reassign owning dentist" onClose={onClose}>
      <Field label="New owning dentist">
        <select
          value={toDentistId}
          onChange={(e) => setToDentistId(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400"
          data-testid="reassign-doctor-select"
        >
          <option value="">Select a dentist…</option>
          {candidates.map((d) => (
            <option key={d.id} value={d.id}>{doctorDisplayName(d)}</option>
          ))}
        </select>
      </Field>
      <Field label="Reason">
        <select
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400"
          data-testid="reassign-reason-select"
        >
          {REASON_OPTIONS.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
      </Field>
      <Field label="Notes (optional)">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400"
          placeholder="Context for the audit log"
          data-testid="reassign-notes"
        />
      </Field>
      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="px-3 py-1.5 text-xs font-semibold rounded-lg text-slate-600 hover:bg-slate-100"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={busy || !toDentistId}
          data-testid="reassign-submit"
          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          Reassign
        </button>
      </div>
    </ModalShell>
  );
}

function RequestModal({ patient, currentOwnerId, onClose, onDone }) {
  const doctors = useDoctorList();
  const [toDentistId, setToDentistId] = useState("");
  const [reason, setReason]           = useState("patient_request");
  const [notes, setNotes]             = useState("");
  const [busy, setBusy]               = useState(false);

  const candidates = doctors.filter((d) => d.id !== currentOwnerId);

  async function handleSubmit() {
    if (!toDentistId) {
      toast.error("Pick a dentist.");
      return;
    }
    setBusy(true);
    const { error } = await supabase.rpc("create_ownership_transfer_request", {
      p_patient_id:     patient.id,
      p_to_dentist_id:  toDentistId,
      p_reason:         reason,
      p_notes:          notes?.trim() || null,
    });
    setBusy(false);
    if (error) {
      toast.error(error.message ?? "Could not submit request.");
      return;
    }
    toast.success("Reassignment request submitted.");
    onDone?.();
  }

  return (
    <ModalShell title="Request owner reassignment" onClose={onClose}>
      <p className="text-xs text-slate-500">
        Submits a pending request for an admin to approve. The owning dentist on file does not change until approval.
      </p>
      <Field label="Proposed new owning dentist">
        <select
          value={toDentistId}
          onChange={(e) => setToDentistId(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400"
          data-testid="request-doctor-select"
        >
          <option value="">Select a dentist…</option>
          {candidates.map((d) => (
            <option key={d.id} value={d.id}>{doctorDisplayName(d)}</option>
          ))}
        </select>
      </Field>
      <Field label="Reason">
        <select
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400"
          data-testid="request-reason-select"
        >
          {REASON_OPTIONS.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
      </Field>
      <Field label="Notes (optional)">
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400"
          placeholder="Context for the admin reviewing this request"
          data-testid="request-notes"
        />
      </Field>
      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="px-3 py-1.5 text-xs font-semibold rounded-lg text-slate-600 hover:bg-slate-100"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={busy || !toDentistId}
          data-testid="request-submit"
          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          Submit request
        </button>
      </div>
    </ModalShell>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">{label}</span>
      {children}
    </label>
  );
}

function doctorDisplayName(doctor) {
  return formatDoctorName(doctor);
}
